import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormArray, Validators, FormControl, FormGroupDirective, ValidatorFn, ValidationErrors, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatSelectionList } from '@angular/material/list';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';
import { MatTable } from '@angular/material/table';
import { DialogsService } from 'app/_services/dialogs/dialogs.service';
import { LegislationService } from 'app/_services/legislation.service';
import { NotificationService } from 'app/_services/notification.service';
import { UserService } from 'app/_services/user.service';
import { UserInfo } from 'app/_models';
import { forkJoin, map, Observable } from 'rxjs';
import { LegislationGroup, LegislationItem, LegislationItemRecipient, LegislationRecipientByGroup, LegislationRecipientVM } from '../legislation-items/legislation';
import { ToolsService } from 'app/_services/tools.service';

@Component({
    selector: 'app-add-edit-item',
    templateUrl: './add-edit-item.component.html',
    styleUrls: ['./add-edit-item.component.scss'],
})
export class AddEditItemComponent implements OnInit {
    user: UserInfo = null;
    showError = false;
    disabled = true;
    completed = false;
    isLoading = true;
    state: string;
    itemId: number;
    resourceDS: any = [];
    trackingDS: any = [];
    recipients: LegislationRecipientVM[];
    itemRecipients: LegislationItemRecipient[];
    groups: LegislationGroup[];
    recipientsByGroups: LegislationRecipientByGroup[];
    legislationItemNames: string[];
    statusIsChecked = false;

    errorMatcher = new CustomErrorStateMatcher();

    additionalResourcesColumns = ['sortOrder', 'resourceLabel', 'resourceLink', 'actions'];
    trackingLinksColumns = ['sortOrder', 'trackingLabel', 'trackingLink', 'actions'];

    generalInfoForm = this.fb.group({
        title: ['', [Validators.required, isNameTaken('Test', ['Test1', 'Test2'])]], //TODO: uncomment this to restore validation
        about: ['', Validators.required],
        ourPosition: ['', Validators.required]
    });
    resourceLinksForm = this.fb.group({
        trackingLinks: this.fb.array([]),
        resourceLinks: this.fb.array([]),
    });
    emailOptionsForm = this.fb.group({
        subjectOptions: ['1'],
        emailSubject: [{value: '', disabled: this.disabled}, Validators.required],
        recipient: ['', Validators.required],
        talkingPoints: this.fb.array([], [Validators.required]),
    });
    groupsListForm = new FormControl();
    stepperOrientation: Observable<StepperOrientation>;

    // #region - Getters
    get title() {
        return this.generalInfoForm.get('title') as FormControl;
    }
    get about() {
        return this.generalInfoForm.get('about') as FormControl;
    }
    get ourPosition() {
        return this.generalInfoForm.get('ourPosition') as FormControl;
    }
    get trackingLinks() {
        return this.resourceLinksForm.get('trackingLinks') as FormArray;
    }
    get resourceLinks() {
        return this.resourceLinksForm.get('resourceLinks') as FormArray;
    }
    get subjectOptions() {
        return this.emailOptionsForm.get('subjectOptions') as FormControl;
    }
    get emailSubject() {
        return this.emailOptionsForm.get('emailSubject') as FormControl;
    }
    get recipient() {
        return this.emailOptionsForm.get('recipient') as FormControl;
    }
    get talkingPoints() {
        return this.emailOptionsForm.get('talkingPoints') as FormArray;
    }
    // #endregion

    @Input('injectedData') injectedData: LegislationItem = null;
    @Input('creatingNew') creatingNew: boolean = true;
    @Output() goToListView = new EventEmitter<boolean>();

    @ViewChild('generalInfoFGD') private generalInfoFGD: FormGroupDirective;
    @ViewChild('resourceLinksFGD') private resourceLinksFGD: FormGroupDirective;
    @ViewChild('emailOptionsFGD') private emailOptionsFGD: FormGroupDirective;
    @ViewChild('tlTable') tlTable: MatTable<Element>;
    @ViewChild('arTable') arTable: MatTable<Element>;
    @ViewChild('stepper') private stepper: MatStepper;

    @ViewChild('allSelected', {static: false}) private allSelected: MatSelectionList; 
  

    constructor(
        private cdRef: ChangeDetectorRef,
        private fb: FormBuilder, 
        breakpointObserver: BreakpointObserver, 
        private legislationService: LegislationService,
        private dialogService: DialogsService,
        private notificationService: NotificationService,
        private toolsService: ToolsService,
        private userService: UserService,
    ) {
        const self = this;
        self.user = self.userService.UserValue;
        self.stepperOrientation = breakpointObserver.observe('(min-width: 800px)')
            .pipe(map(({matches}) => matches ? 'horizontal' : 'vertical'));
    }
    

    ngOnInit() {
        const self = this;
        let itemId = 0;
        if (self.injectedData) {
            itemId = self.injectedData.legislationItemId;
        }

        let legislationItems = self.legislationService.getLegislationItems();
        let recipients = self.legislationService.getLegislationRecipients();
        let groups = self.legislationService.getLegislationGroups();
        let recipientsByGroups = self.legislationService.getLegislationRecipientsByGroups();
        let itemRecipients = self.legislationService.getLegislationItemRecipientsByItemId(itemId);

        forkJoin([recipients, groups, recipientsByGroups, itemRecipients, legislationItems]).subscribe(([recipients, groups, recipientsByGroups, itemRecipients, legislationItems]) => {
            self.recipients = recipients;
            self.groups = groups;
            self.recipientsByGroups = recipientsByGroups;
            self.itemRecipients = itemRecipients;
            self.legislationItemNames = legislationItems.map(item => item.title);

            if (self.injectedData) {
                self.loadData(self.injectedData);
            }

            self.isLoading = false;
        });
    }

    ngAfterViewInit(): void {
        // Trigger change detection for loading data into MatTable<Element>
        this.cdRef.detectChanges();
    }

    // #region LOADING DATA
    loadData(data: LegislationItem): void {
        const self = this;
        self.title.setValue(data.title);
        self.about.setValue(data.about);
        self.ourPosition.setValue(data.ourPosition);

        self.setTrackingLinksData(data.trackingLinks);
        self.setResourceLinksData(data.resourceLinks);
        self.setTalkingPointsData(data.talkingPoints);
        self.setGroupListData();
        self.setRecipientData();

        data.isEmailSubjectTitle ? self.subjectOptions.setValue('1') : self.subjectOptions.setValue('2');

        self.statusIsChecked = data.active;
    }

    setTrackingLinksData(trackingLinks): void {
        const self = this;
        
        if(trackingLinks) {
            trackingLinks.forEach(item => {
                self.trackingLinks.push(
                    self.fb.group({
                        trackingLinkId: [item.trackingLinkId],
                        trackingLabel: [item.trackingLabel, Validators.required],
                        trackingLink: [item.trackingLink, Validators.required],
                        displayText: [item.displayText],
                        legislationItemId: [item.legislationItemId],
                    })
                );
            });
        }
    }

    setResourceLinksData(resourceLinks): void  {
        const self = this;

        if(resourceLinks) {
            resourceLinks.forEach(item => {
                self.resourceLinks.push(
                    self.fb.group({
                        resourceLinkId: [item.resourceLinkId],
                        resourceLabel: [item.resourceLabel, Validators.required],
                        resourceLink: [item.resourceLink, Validators.required],
                        displayText: [item.displayText],
                        legislationItemId: [item.legislationItemId],
                    })
                );
            });
        }
    }

    setTalkingPointsData(talkingPoints): void {
        const self = this;
        
        talkingPoints.forEach(item => {
            self.talkingPoints.push(
                self.fb.group({
                    talkingPointId: [item.talkingPointId],
                    talkingPoint: [item.talkingPoint, Validators.required],
                    legislationItemId: [item.legislationItemId],
                })
            );
        });
    }

    setGroupListData(): void {
        const self = this;
        const groups: LegislationRecipientByGroup[] = [];

        const id_filter = self.itemRecipients.map(ir => ir.recipientId);

        self.recipientsByGroups.forEach( rbg => {
            const selectedGroups = rbg.recipients.filter(recipient => id_filter.includes(recipient.recipientId));
            if (selectedGroups.length === rbg.recipients.length) {
                groups.push(rbg);
            }
        });

        self.groupsListForm.setValue(groups);
    }

    setRecipientData(): void {
        const self = this;
        const id_filter = self.itemRecipients.map(ir => ir.recipientId);
        const result = self.recipients.filter(recipient => id_filter.includes(recipient.recipientId));

        self.recipient.setValue(result);
    }

    // #endregion

    isAllSelected(): boolean {
        const numSelected = this.allSelected.selectedOptions.selected.length;
        const numRecipients = this.recipients.length;
        return numSelected === numRecipients;
    }

    masterToggle(): void {
        if (this.isAllSelected()) {
          this.allSelected.deselectAll();
          return;
        }

        this.allSelected.selectAll();
    }

    loadTestForm(): void {
        const self = this;

        self.title.setValue('Citizens Request Opposing HB 7045: School Choice');

        self.about.setValue('HB 7045 expands the number of vouchers and the negative financial impact to school districts. The bill repeals the GSP transitioning students to the Family Empowerment Scholarship Program and repeals the McKay Scholarship effective July 1, 2022, transitioning students to the Family Empowerment Scholarship Program and revises the scholarship amount for McKay. The bill revises the requirements for the FES by: -Expanding program eligibility by removing the prior public attendance requirement and eliminating the need for scholarship students to renew program participation for students seeking an educational choice scholarship. -Excluding students from the maximum number of students participating if they are a dependent child of a member of the Armed Forces, a foster child, or an adopted child; or meet certain eligibility requirements and attended public school in the prior school year. In addition, up to 15,000 FTC students whose scholarship was not renewed due to a decrease in contributions are also excluded from the program maximum. -Expanding eligibility to students with disabilities currently authorized under McKay and GSP. -Revising funding of scholarships for students with disabilities under the FES program to the Florida Education Finance Program with calculations dependent on the students eligibility. -Establishes an initial maximum program capacity of 20,000 students, annually increasing by 1 percent of the state’s total exceptional student education full-time equivalent enrollment. However, students who received a GSP in the 2020-2021 school year receive the greater of the prior award amount or the new award amount. -Excluding students from the maximum number of students participating if they are a student with a disability who: received specialized instructional services in VPK; attended public school in the prior school year; are a dependent child of a member of the Armed Forces, a foster child, or an adopted child; and students who received a McKay Scholarship in the 2021-2022 school year. The bill revises the frequency of operational audits by the Auditor General from every year to at least once every 3 years for the FTC. The bill increases the scholarship amount for the FTC, FES, and HSP to the full amount provided for the student, from the current 95 percent of the calculated amount and lowers the scholarship funding organizations administrative fee from 3 percent to 2.5 percent. The bill would have an indeterminate fiscal impact.');

        self.ourPosition.setValue('The most pressing concern is the continued expansion of vouchers and the lack of accountability of these vouchers.The District strongly feels that the same accountability measures should be enforced equally to all schools that receive public funds. Public funds should not be diverted to unaccountable private schools. The public deserves to have the same information regarding the performance of all publicly funded schools.');

        self.trackingLinks.push(
            self.fb.group({
                trackingLinkId: [0],
                trackingLabel: ['House', Validators.required],
                trackingLink: ['https://www.myfloridahouse.gov/Sections/MyHouse/login.aspx', Validators.required],
                displayText: [null],
                legislationItemId: [0],
            })
        );
        self.resourceLinks.push(
            self.fb.group({
                resourceLinkId: [0],
                resourceLabel: ['Resc1', Validators.required],
                resourceLink: ['https://webappe.browardschools.com/legislativeupdates/BILL%20TEXT:%20https://www.myfloridahouse.gov/Sections/Documents/loaddoc.aspx?FileName=PCB%20EEC%2021-01.DOCX&DocumentType=Proposed%20Committee%20Bills%20(PCBs)&Session=2021&CommitteeId=3105', Validators.required],
                displayText: [null],
                legislationItemId: [0],
            })
        );

        self.resourceLinks.push(
            self.fb.group({
                resourceLinkId: [0],
                resourceLabel: ['Resc2', Validators.required],
                resourceLink: ['https://webappe.browardschools.com/legislativeupdates/STAFF%20ANALYSIS:%20https://www.myfloridahouse.gov/Sections/Documents/loaddoc.aspx?FileName=pcb01a.EEC.DOCX&DocumentType=Analysis&CommitteeId=3105&Session=2021', Validators.required],
                displayText: [null],
                legislationItemId: [0],
            })
        );

        self.talkingPoints.push(
            this.fb.group({
                talkingPointId: [0],
                talkingPoint: ['I strongly oppose this bill. The most pressing concern is the continued expansion of vouchers and the lack of accountability of these vouchers. I strongly feel that the same accountability measures should be enforced equally to all schools that receive public funds. The public deserves to have the same information regarding the performance of all publicly funded schools.', Validators.required],
                legislationItemId: [0],
            })
        );
        self.talkingPoints.push(
            this.fb.group({
                talkingPointId: [0],
                talkingPoint: ['As a taxpayer, I strongly feel that public funds should not be diverted to unaccountable private schools. The public deserves to have the same information regarding the performance of all publicly funded schools', Validators.required],
                legislationItemId: [0],
            })
        );
        self.talkingPoints.push(
            this.fb.group({
                talkingPointId: [0],
                talkingPoint: ['I Strongly oppose this bill. The legislature should not be diverting public funds to private schools. Instead, the legislature should fully fund all public schools.', Validators.required],
                legislationItemId: [0],
            })
        );
    }

    onToggleSubject(event): void {
        const self = this;
        event.value === '1' ? self.emailSubject.disable() : self.emailSubject.enable();
    }

    moveStep(index: number): void {
        const self = this;
        self.stepper.selectedIndex = index;
    }

    confirmReset(): void {
        const self = this;
        const dialogResult$ = self.dialogService.OpenConfirmationDialog({
            TitleText: 'Reset Forms',
            MessageText: 'Are you sure you want to reset these forms?',
            OKText: 'Yes, reset forms',
            CancelText: 'No',
        });

        dialogResult$.subscribe((result: boolean) => {
            if (!result) {
                return;
            }
            self.resetForms();
        });
    }

    resetForms(): void {
        const self = this;

        // Reset stepper, which also resets form values
        self.stepper.reset(); 

        // Clear and reset FormArray controls
        self.trackingLinks.clear();
        self.trackingLinks.reset();
        self.resourceLinks.clear();
        self.resourceLinks.reset();
        self.talkingPoints.clear();
        self.talkingPoints.reset();

        // Reset entire forms via FormGroupDirectives
        self.emailOptionsFGD.resetForm();
        self.resourceLinksFGD.resetForm();
        self.generalInfoFGD.resetForm();

        // Set subject options to default to using Title as subject.
        self.subjectOptions.setValue('1');
        self.revertEmailSubjectStatuses();
    }

    compareRecipients(c1: { name: string }, c2: { name: string }): boolean {
        return c1 && c2 && c1.name === c2.name;
    }

    compareGroups(c1: { groupName: string }, c2: { groupName: string }): boolean {
        return c1 && c2 && c1.groupName === c2.groupName;
    }

    backToListOfLegislationItems(): void {
        this.goToListView.emit(true);
    }

    completeStepper(): void {
        const self = this;
        self.stepper.selected.completed = true;
        self.stepper.next();
    }

    prepareRecipientsForDB(): LegislationRecipientVM[] {
        const self = this;
        const recipients: LegislationRecipientVM[] = []; 

        // In the Group tab under Recipients, for each group selected we add the group's recipients to the array.
        if (self.groupsListForm.value && self.groupsListForm.value.length > 0) {
            
            self.groupsListForm.value.forEach((g: LegislationRecipientByGroup )=> {
                console.log(g);
                g.recipients.forEach((rg: LegislationRecipientVM) => {
                    const recip = self.recipients.find(x => x.recipientId === rg.recipientId);
                    recipients.push(recip)
                });
            });
        }

        // In the Individual tab under Recipients, add each selected recipient to the array.
        if (self.recipient.value && self.recipient.value.length > 0) {
            self.recipient.value.forEach((r: LegislationRecipientVM) => {
                const recip = self.recipients.find(x => x.recipientId === r.recipientId);
                recipients.push(recip);
            });
        }
        
        // Remove duplicates recipients from the array.
        const uniqueRecipients = Array.from( new Set(recipients.map((recip) => recip) ));

        return uniqueRecipients;
    }

    prepareItemRecipientsForDB(creatingNew: boolean, legislationItemId: number, recipients: LegislationRecipientVM[]): LegislationItemRecipient[] {
        const self = this;
        const selectedItemRecipients: LegislationItemRecipient[] = [];
        let id = 0;
        
        // Create array of LegislaitonItemRecipients to be added to the array.
        recipients.forEach(r => {
            if (!creatingNew) {
                const itemRecipient = self.itemRecipients.find(x => x.recipientId === r.recipientId);
                id = itemRecipient ? itemRecipient.itemRecipientId : 0; // Set to 0 since this is set to Identity column in database
            }

            const newItemRecipient: LegislationItemRecipient = {
                itemRecipientId: id,
                recipientId: r.recipientId,
                legislationItemId: legislationItemId,
            }
            selectedItemRecipients.push(newItemRecipient);
        });

        return selectedItemRecipients;
    }

    prepareLegislationItem(creatingNew?: boolean, legislationItem?: LegislationItem): LegislationItem {
        const self = this;
        const sapid = self.user.sapid.toString();

        // Set values from (most) forms to their respective properties for the legislation item.
        const newItem: LegislationItem = {
            legislationItemId: creatingNew ? 0 : legislationItem.legislationItemId,
            title: self.title.value,
            about: self.about.value,
            ourPosition: self.ourPosition.value,
            emailSubject: self.subjectOptions.value ? self.title.value : self.emailSubject.value,
            createdBySapid: sapid,
            updatedBySapid: creatingNew ? '' : sapid,
            dateCreated: creatingNew ? this.toolsService.toISOLocal(new Date()) : legislationItem.dateCreated,
            dateUpdated: creatingNew ? '' : this.toolsService.toISOLocal(new Date()),
            active: self.statusIsChecked,
        }

        return newItem;
    }

    prepareLegislationItemDetails(legislationItemId: number, newItem: LegislationItem): LegislationItem {
        const self = this;

        newItem.trackingLinks = self.trackingLinks.value;
        newItem.resourceLinks = self.resourceLinks.value;
        newItem.talkingPoints = self.talkingPoints.value;

        newItem.trackingLinks.forEach(trackingLink => {
            trackingLink.legislationItemId = legislationItemId;
        });
        newItem.resourceLinks.forEach(resourceLink => {
            resourceLink.legislationItemId = legislationItemId;
        });
        newItem.talkingPoints.forEach(talkingPoint => {
            talkingPoint.legislationItemId = legislationItemId;
        });
        
        const recipients = self.prepareRecipientsForDB();
        const itemRecipients = self.prepareItemRecipientsForDB(self.creatingNew, legislationItemId, recipients);

        // Assign itemRecipients to cooresponding legislaiton item.
        newItem.itemRecipients = itemRecipients;

        return newItem;
    }

    addItem(): void {
        const self = this;
        const newItem = self.prepareLegislationItem(self.creatingNew, self.injectedData);

        self.legislationService.createLegislationItem(newItem).subscribe((result: LegislationItem) => {
            newItem.legislationItemId = result.legislationItemId;
            const newItemWithDetails = self.prepareLegislationItemDetails(result.legislationItemId, newItem);

            self.legislationService.createLegislationItemDetails(newItemWithDetails).subscribe(() => {
                    // These are not needed due to lazy loading of tabs, which will upadte the list of items anyways
                    // self.legislationitems.push(res); 
                    // this.loadDatasource(self.legislationItems);
        
                    self.notificationService.showSuccess(`New legislation item has been added.`);
                    self.completeStepper();
            });
        });
    }

    editItem(): void {
        const self = this;
        const item = self.prepareLegislationItem(self.creatingNew, self.injectedData);
              
        self.legislationService.updateLegislationItem(item.legislationItemId, item).subscribe((result: LegislationItem) => {
            const newItemWithDetails = self.prepareLegislationItemDetails(item.legislationItemId, item);

            self.legislationService.updateLegislationItemDetails(item.legislationItemId, newItemWithDetails).subscribe(() => {
                    self.notificationService.showSuccess(`Legislation item has been updated.`);
                    self.completeStepper();
            });
        });
    }

    // #region Tracking Links
    createTrackingLink(): void {
        const self = this;
        // const count = self.trackingLinks.length + 1;
        self.trackingLinks.push(
            self.fb.group({
                // trackingLabel: 'Resc' + count + ':',
                trackingLinkId: [0],
                trackingLabel: ['Resc', Validators.required],
                trackingLink: ['', Validators.required],
                displayText: [''],
                legislationItemId: [0],
            })
        );
        
        self.tlTable.renderRows();
    }

    deleteTrackingLink(index: number): void {
        const self = this;
        self.trackingLinks.removeAt(index);
        self.tlTable.renderRows();
    }
    // #endregion

    // #region Resource Links
    createResource(): void {
        const self = this;
        self.resourceLinks.push(
            self.fb.group({
                resourceLinkId: [0],
                resourceLabel: ['Resc', Validators.required],
                resourceLink: ['', Validators.required],
                displayText: [''],
                legislationItemId: [0],
            })
        );
        self.arTable.renderRows();
    }

    deleteResource(index: number): void {
        const self = this;
        self.resourceLinks.removeAt(index);
        self.arTable.renderRows();
    }
    // #endregion

    // #region Email Subject
    revertEmailSubjectStatuses(): void {
        const self = this;
        self.emailSubject.markAsPristine();
        self.emailSubject.markAsUntouched();
    }
    // #endregion

    // #region Talking Points
    createTalkingPoint(): void {
        const self = this;
        self.talkingPoints.push(
            this.fb.group({
                talkingPointId: [0],
                talkingPoint: ['', Validators.required],
                legislationItemId: [0],
            }, 
            // {updateOn: blur}
            )
        );
    }

    deleteTalkingPoint(index: number): void {
        const self = this;
        self.talkingPoints.removeAt(index);
    }
    // #endregion

    // #region Error Messages
    getTitleErrorMessage(): string {
        const self = this;
        if (self.title.hasError('required')) {
          return 'Missing title';
        }
    
        return self.title.hasError('isNameTaken') ? 'This title name has already been taken' : '';
    }
    //#endregion
}

/* Validation function to see if the inputted CategoryName already exists. If so, the field will become invalid.
 * This error will NOT occurr if, while editing an existing CategoryName, the user reverts changes to match the
 * original name provide to the form prior to saving.
 */
function isNameTaken(name: string, listOfNames: string[]): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
        let error = null; // In case no names exist in the database
        if (listOfNames) {
            const isNameTaken = listOfNames.some((value) => value === control.value);
            error = isNameTaken && control.dirty && control.value !== name ? { isNameTaken: true } : null;
        }
        return error;
    };
}

/* Since we're dealing with FormArrays, some fields outside of the FormArray will trigger validation  
 * checks whenever new items are added to the FormArray, even when the field itself is untouched.
 * So we add a custom ErrorStateMatcher and add the following property to the affected fields:
 *      [errorStateMatcher]="errorMatcher"
 * This will force the field to only show error when the field has the proper conditions.
 */
export class CustomErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
      const isSubmitted = form && form.submitted;
      return (control && control.invalid && (control.dirty || control.touched));
    }
}